"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { X } from "lucide-react";
import { businessFaqs, homeFaqs } from "@/lib/platform";
import { ChatBody } from "./ChatBody";
import { IonOrb } from "./IonOrb";
import styles from "./ChatLauncher.module.css";

const FAQS = [...businessFaqs, ...homeFaqs];

/**
 * The floating chat: an orb at the bottom right that opens the conversation in
 * a panel. It steps aside while a FAQ chat is on screen, so the page never
 * shows two of them, and closes itself if the visitor scrolls to one.
 */
export function ChatLauncher() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [atFaq, setAtFaq] = useState(false);
  const [hint, setHint] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const orbRef = useRef<HTMLButtonElement>(null);

  // Watch for a FAQ chat on this page, and start each page with the orb showing.
  useEffect(() => {
    setAtFaq(false);
    setOpen(false);
    const targets = document.querySelectorAll("[data-faq-chat]");
    if (!targets.length) return;
    const io = new IntersectionObserver((entries) => {
      const visible = entries.some((e) => e.isIntersecting);
      setAtFaq(visible);
      if (visible) setOpen(false);
    });
    targets.forEach((t) => io.observe(t));
    return () => io.disconnect();
  }, [pathname]);

  // A brief label after the page settles, once per visit, then just the orb.
  useEffect(() => {
    let seen = false;
    try {
      seen = sessionStorage.getItem("chat-hint") === "1";
      sessionStorage.setItem("chat-hint", "1");
    } catch {
      /* Private windows can refuse storage; the label just shows again. */
    }
    if (seen) return;
    const on = window.setTimeout(() => setHint(true), 1800);
    const off = window.setTimeout(() => setHint(false), 6800);
    return () => {
      window.clearTimeout(on);
      window.clearTimeout(off);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    setHint(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      orbRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    const focus = window.setTimeout(() => inputRef.current?.focus(), 250);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.clearTimeout(focus);
    };
  }, [open]);

  return (
    <div className={styles.root}>
      <section
        className={styles.panel}
        data-open={open}
        role="dialog"
        aria-label="Chat with ION"
        aria-hidden={!open}
        inert={!open}
      >
        <header className={styles.head}>
          <IonOrb size={34} />
          <span className={styles.headText}>
            <strong>Ask ION</strong>
            <small>Answers from our website</small>
          </span>
          <button type="button" className={styles.close} aria-label="Close chat" onClick={() => setOpen(false)}>
            <X className="h-4 w-4" />
          </button>
        </header>
        <ChatBody ref={inputRef} faqs={FAQS} compact />
      </section>

      <div className={styles.launcher} data-hidden={open || atFaq}>
        <span className={styles.hint} data-show={hint} aria-hidden="true">
          Ask ION anything
        </span>
        <button
          ref={orbRef}
          type="button"
          className={styles.orb}
          aria-label="Open chat with ION"
          aria-expanded={open}
          tabIndex={open || atFaq ? -1 : 0}
          onClick={() => setOpen(true)}
        >
          <IonOrb size={34} className={styles.orbImg} />
        </button>
      </div>
    </div>
  );
}
